import React, { useLayoutEffect, useMemo, useRef } from "react";
import { useMsal } from "@azure/msal-react";
import { ApolloClient, ApolloLink, ApolloProvider, Observable, createHttpLink } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { accountSessionKey, acquireAuthenticationToken, isInteractionRequiredError } from "../authUtils";
import { notifyInteractionRequired } from "../authEvents";
import registerMyAccountMutation from "../graphql/mutations/registerMyAccount";

const REGISTRATION_DEADLINE_MS = 15000;
const REGISTRATION_WARNING = "Verified sign-in registration failed.";

// One client/cache per account scope. Clearing a shared cache is insufficient:
// an old in-flight response can repopulate it after an account switch.
export function createApolloSession({ instance, account, config, scopes, createCache, fetch }) {
  const key = accountSessionKey(account);
  let active = true;
  let registration;
  let registrationController;
  const isCurrent = () => active && accountSessionKey(instance.getActiveAccount()) === key;
  const requireCurrent = () => {
    if (!isCurrent()) {
      throw new Error("The application account changed");
    }
  };
  const boundary = new ApolloLink((operation, forward) => new Observable((observer) => {
    if (!isCurrent()) {
      observer.error(new Error("The application account changed"));
      return undefined;
    }
    const subscription = forward(operation).subscribe({
      next: (value) => isCurrent()
        ? observer.next(value)
        : observer.error(new Error("The application account changed")),
      error: (error) => observer.error(isCurrent() ? error : new Error("The application account changed")),
      complete: () => isCurrent()
        ? observer.complete()
        : observer.error(new Error("The application account changed")),
    });
    return () => subscription.unsubscribe();
  }));
  const auth = setContext(async (_, { headers, fetchOptions }) => {
    requireCurrent();
    // Neither caller headers nor a previous context may choose credentials.
    const cleanHeaders = Object.fromEntries(Object.entries(headers || {}).filter(
      ([name]) => !["authorization", "x-api-key"].includes(name.toLowerCase()),
    ));
    if (!account) {
      return { headers: { ...cleanHeaders, "x-api-key": config.API_KEY } };
    }
    try {
      const token = await acquireAuthenticationToken(instance, account, scopes);
      requireCurrent();
      // The deadline may expire while MSAL is still acquiring the token.
      if (fetchOptions?.signal?.aborted) {
        throw new Error("The application request was interrupted");
      }
      return { headers: { ...cleanHeaders, authorization: `Bearer ${token}` } };
    } catch (error) {
      if (isCurrent() && isInteractionRequiredError(error)) {
        notifyInteractionRequired(error);
      }
      // Employee auth failures never retry using the guest portal key.
      throw error;
    }
  });
  const client = new ApolloClient({
    link: boundary.concat(auth).concat(createHttpLink({ uri: config.GRAPHQL_URL, fetch })),
    cache: createCache(),
  });
  return {
    key,
    client,
    registerMyAccount() {
      if (!account || !isCurrent()) {
        return Promise.resolve(false);
      }
      // The settled promise also deduplicates failures: there is no retry loop.
      if (!registration) {
        const controller = new AbortController();
        registrationController = controller;
        registration = (async () => {
          let timer;
          const interrupted = new Promise((_, reject) => {
            controller.signal.addEventListener("abort", () => reject(new Error("Registration interrupted")), { once: true });
            timer = setTimeout(() => controller.abort(), REGISTRATION_DEADLINE_MS);
          });
          try {
            const result = await Promise.race([interrupted, client.mutate({
              mutation: registerMyAccountMutation,
              fetchPolicy: "no-cache",
              errorPolicy: "none",
              context: { fetchOptions: { signal: controller.signal } },
            })]);
            if (!isCurrent()) {
              return false;
            }
            if (controller.signal.aborted || result.errors?.length || result.data?.registerMyAccount !== true) {
              throw new Error("Registration was not confirmed");
            }
            return true;
          } catch {
            if (isCurrent()) {
              // Transport/auth errors may contain credentials or private claims.
              console.warn(REGISTRATION_WARNING);
            }
            return false;
          } finally {
            clearTimeout(timer);
            registrationController = null;
          }
        })();
      }
      return registration;
    },
    dispose() {
      if (!active) {
        return;
      }
      active = false;
      registrationController?.abort();
      client.stop();
    },
  };
}

export default function ApolloSessionProvider({ config, scopes, createCache, children }) {
  const { instance, accounts } = useMsal();
  const account = accounts.length ? instance.getActiveAccount() : null;
  const key = accountSessionKey(account);
  const session = useMemo(
    () => createApolloSession({ instance, account, config, scopes, createCache }),
    // Identity, not the refreshed AccountInfo object, defines the session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [instance, key, config, scopes, createCache],
  );
  const pendingDisposal = useRef(null);
  useLayoutEffect(() => {
    if (pendingDisposal.current?.session === session) {
      pendingDisposal.current.cancelled = true;
    }
    void session.registerMyAccount();
    return () => {
      const pending = { session, cancelled: false };
      pendingDisposal.current = pending;
      // StrictMode's cleanup/setup replay keeps this same client and attempt.
      // Actual unmounts/account changes still dispose at the next microtask.
      queueMicrotask(() => {
        if (!pending.cancelled) {
          session.dispose();
        }
        if (pendingDisposal.current === pending) {
          pendingDisposal.current = null;
        }
      });
    };
  }, [session]);
  return <ApolloProvider key={key} client={session.client}>{children}</ApolloProvider>;
}